<template>
  <v-container class="grey lighten-5" fluid>
    <v-row no-gutters>
      <v-col cols="12">
        <v-data-table
          :headers="headers"
          :items="items"
          dense
          class="elevation-1"
          :loading="loading"
          :single-expand="false"
          :expanded.sync="expanded"
          show-expand
        >
          <template v-slot:top>
            <v-toolbar color="white" class="mb-1" flat>
              <v-icon large>mdi-account</v-icon>
              <v-divider vertical class="mx-2"></v-divider>
              <v-toolbar-title>Utilizatori</v-toolbar-title>
              <v-spacer></v-spacer>
              <v-divider vertical class="mx-2"></v-divider>
              <v-btn icon @click="openUserDialog">
                <v-icon color="primary">mdi-plus</v-icon>
              </v-btn>
            </v-toolbar>
            <v-divider></v-divider>
          </template>

          <template v-slot:item.id="{ item }">
            <div style="width: 30px" class="text-truncate">{{ item.id }}</div>
          </template>
          <template v-slot:item.deactivated="{ item }">
            <v-icon :color="item.deactivated ? 'error' : 'success'">
              {{ item.deactivated ? 'mdi-close' : 'mdi-check'}}
            </v-icon>
          </template>
          <template v-slot:item.created="{ item }">
            {{ new Date(item.created).toLocaleString("ro-RO") }}
          </template>
          <template v-slot:item.ciUrl="{ item }">
            <a :href="item.ciUrl" download target="_blank" v-if="item.ciUrl">
              <v-icon color="info">mdi-download</v-icon>
            </a>
          </template>
          <template v-slot:item.hasCert="{ item }">
            <v-icon :color="item.hasCert ? 'success' : 'error'">
              {{ item.hasCert ? "mdi-check" : "mdi-close" }}
            </v-icon>
          </template>
          <template v-slot:item.certUrl="{ item }">
            <a :href="item.certUrl" download target="_blank" v-if="item.certUrl">
              <v-icon color="info">mdi-download</v-icon>
            </a>
          </template>
          <template v-slot:item.role="{ item }">
            <v-icon color="success" v-if="item.role==='admin'" title="admin">mdi-shield-account</v-icon>
            <v-icon color="info" v-if="item.role==='evaluator'" title="evaluator">mdi-account-cowboy-hat</v-icon>
            <v-icon color="info" v-if="item.role==='el presidente'" title="presedinte comisie">mdi-chess-king</v-icon>
            <v-icon color="gray" v-if="item.role==='aplicant'" title="aplicant">mdi-account</v-icon>
          </template>

          <template v-slot:item.evaluat="{ item }">
            <template v-if="item.evaluare">
              <template v-for="(ev, index) in item.evaluare">
                <div :key="index">
                  {{ ev.evaluator }}
                  -
                  {{ ev.comisie }}
                  -
                  {{ new Date(ev.data).toLocaleString("ro-RO") }}
                </div>
              </template>
            </template>
            <template v-else-if="item.role === 'aplicant'">
              <v-icon color="error">mdi-close</v-icon>
            </template>
            <template v-else>-</template>
          </template>

          <template v-slot:item.admis="{ item }">
            <template v-if="item.evaluare">
              <v-icon v-for="(ev, index) in item.evaluare" :key="index" :color="ev.admis ? 'primary' : 'error'" :title="`${ev.evaluator} - ${ev.comisie}`">
                {{ ev.admis ? 'mdi-check' : 'mdi-close' }}
              </v-icon>
            </template>
          </template>

          <template v-slot:item.scor="{ item }">
            <template v-if="item.evaluare">
              <template v-for="(ev, index) in item.evaluare">
                <div :key="index" style="min-width: 100px" class="my-2">
                  <v-progress-linear
                    :value="totalScore(ev.score)"
                    color="blue-grey lighten-4"
                    height="16"
                    :title="`${ev.evaluator} - ${ev.comisie}`">
                    <small>Total: {{ totalScore(ev.score) }} pct</small>
                  </v-progress-linear>
                </div>
              </template>
            </template>
          </template>

          <template v-slot:item.medie="{ item }">
            <template v-if="item.evaluare">
              <template v-for="comisie in comisii(item.evaluare)">
                <div :key="comisie.label" style="min-width: 100px" class="my-2">
                  <v-progress-linear
                    :value="comisie.score"
                    color="blue-grey lighten-4"
                    height="16">
                    <small>{{ comisie.label }}: {{ comisie.medie.toFixed(2) }} pct</small>
                  </v-progress-linear>
                </div>
              </template>
            </template>
          </template>

          <template v-slot:item.actions="{ item }">
            <div style="white-space: nowrap">
              <v-icon
                small
                class="mr-2"
                @click="editItem(item)"
                color="accent"
                title="editeaza cont">
                mdi-pencil
              </v-icon>
              <v-icon
                small
                class="mr-2"
                @click="deleteItem(item)"
                :color="item.deactivated ? 'error' : 'success'"
                :title="item.deactivated ? 'activeaza cont' : 'dezactiveaza cont'">
                {{ item.deactivated ? 'mdi-toggle-switch-off' : 'mdi-toggle-switch' }}
              </v-icon>
              <v-icon
                small
                class="mr-2"
                @click="openForm(item)"
                color="warning"
                title="Deblocheaza formularul">
                mdi-format-list-text
              </v-icon>
            </div>
          </template>

          <template v-slot:item.data-table-expand="{ item, isExpanded, expand }">
            <v-icon @click="expandRow(expand, !isExpanded, item.id)">{{ isExpanded ? "mdi-chevron-up" : "mdi-chevron-down"}}</v-icon>
          </template>

          <template v-slot:expanded-item="{ headers, item }">
            <step-board
              :colspan="headers.length"
              :expandedData="expandedData"
              :personalData="personalData"
              :item="item"
              :formKeys="formKeys"
              @reload="reloadForm(item.id)"/>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <v-dialog persistent scrollable v-model="userDialog" width="500">
      <v-card class="pa-md-4 mx-auto" max-height="75vh">
        <v-card-title>
          <h1 class="text-h6 text-center mb-3">Detalii Utilizator</h1>
        </v-card-title>
        <v-card-text>
          <v-text-field
            class="mt-2"
            v-if="!actionItem"
            prepend-icon="mdi-email"
            shaped
            rounded
            solo
            dense
            flat
            outlined
            single-line
            v-model="selectedUser.email"
            label="Email"
            required
            hint="Emailul utilizatorului (folosit la autentificare)"
            persistent-hint
          ></v-text-field>

          <v-text-field
            v-if="!actionItem"
            prepend-icon="mdi-lock"
            shaped
            rounded
            solo
            dense
            flat
            outlined
            single-line
            v-model="selectedUser.password"
            label="Parola"
            required
            hint="Parola utilizatorului (folosita la autentificare, minim 6 caractere)"
            persistent-hint
          ></v-text-field>

          <v-text-field
            prepend-icon="mdi-account-circle"
            shaped
            rounded
            solo
            dense
            flat
            outlined
            single-line
            v-model="selectedUser.nume"
            label="Nume"
            required
            hint="Numele utilizatorului"
            persistent-hint
          ></v-text-field>

          <v-text-field
            shaped
            prepend-icon="mdi-account-supervisor"
            rounded
            solo
            dense
            flat
            outlined
            single-line
            v-model="selectedUser.prenume"
            label="Prenume"
            requiered
            hint="Prenumele utilizatorului"
            persistent-hint
          ></v-text-field>

          <v-text-field
            shaped
            prepend-icon="mdi-identifier"
            rounded
            solo
            dense
            flat
            outlined
            :counter="13"
            single-line
            v-model="selectedUser.cnp"
            label="CNP"
            required
            hint="CNP-ul utilizatorului"
            persistent-hint
          ></v-text-field>

          <v-select
            :items="judeteItems"
            v-model="selectedUser.judet"
            label="Judet"
            rounded
            solo
            dense
            flat
            outlined
            single-line
            shaped
            prepend-icon="mdi-city"
            hint="Judetul utilizatorului"
            persistent-hint
          ></v-select>

          <v-switch
            v-model="selectedUser.hasCert"
            :label="`A parcurs cursul de antreprenoriat?`"
          ></v-switch>

          <v-radio-group v-model="selectedUser.role" label="Rol">
            <v-radio
              v-for="role in ['aplicant', 'evaluator', 'admin', 'el presidente']"
              :key="role"
              :label="role"
              :value="role">
            </v-radio>
          </v-radio-group>

          <v-text-field
            v-if="['evaluator', 'el presidente', 'admin'].includes(selectedUser.role)"
            shaped
            prepend-icon="mdi-account-multiple"
            rounded
            solo
            dense
            flat
            outlined
            :counter="13"
            single-line
            v-model="selectedUser.comisie"
            label="Comisie"
            required
            hint="Doar pentru evaluatori, comisia din care face parte."
            persistent-hint>
          </v-text-field>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeUserDialog">
            Anulare
          </v-btn>
          <v-btn
            large
            color="primary"
            @click="saveDetails"
            :diabled="loading"
            :loading="loading"
          >
            Salvati informatiile
            <template v-slot:loader>
              <span class="custom-loader">
                <v-icon light color="white">mdi-cached</v-icon>
              </span>
            </template>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog persistent scrollable v-model="deleteDialog" width="500">
      <v-card class="pa-md-4 mx-auto" max-height="75vh">
        <v-card-title>
          <h1 class="text-h6 text-center mb-3">
            {{ actionItem && actionItem.deactivated ? 'Activare cont utilizator' : 'Dezactivare cont utilizator' }}
          </h1>
        </v-card-title>
        <v-card-text>
          Sunteti sigur(a) ca doriti sa {{ actionItem && actionItem.deactivated ? "activati" : "dezactivati" }} contul acestui utilizator?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeDelete">Anulare</v-btn>
          <v-btn color="success" @click="confirmDelete">Confirmare</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog persistent scrollable v-model="unlockFormDialog" width="500">
      <v-card class="pa-md-4 mx-auto" max-height="75vh">
        <v-card-title>
          <h1 class="text-h6 text-center mb-3">
            Deblocare formular
          </h1>
        </v-card-title>
        <v-card-text>
          Sunteti sigur(a) ca doriti sa deblocati formularul acestui utilizator?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeUnlockForm">Anulare</v-btn>
          <v-btn color="success" @click="confirmUnlockForm">Confirmare</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import StepBoard from "@/components/StepBoard.vue";

export default {
  name: "admin-users",

  components: {
    "step-board": StepBoard
  },

  data: () => ({
    headers: [
      {
        text: "ID",
        value: "id",
        align: "start",
        sortable: false,
        filterable: false
      },
      {
        text: "NrCrt",
        value: "nrcrt",
        align: "end",
        sortable: true,
        filterable: false
      },
      {
        text: "Activ?",
        value: "deactivated",
        align: "end",
        sortable: true,
        filterable: false
      },
      {
        text: "Email",
        value: "email",
        align: "start",
        sortable: true,
        filterable: false
      },
      {
        text: "Rol",
        value: "role",
        align: "start",
        sortable: true,
        filterable: false
      },
      {
        text: "Comisie",
        value: "comisie",
        align: "start",
        sortable: true,
        filterable: false
      },
      {
        text: "Nume",
        value: "nume",
        align: "start",
        sortable: true,
        filterable: false
      },
      {
        text: "Prenume",
        value: "prenume",
        align: "start",
        sortable: true,
        filterable: false
      },
      {
        text: "Judet",
        value: "judet",
        align: "start",
        sortable: true,
        filterable: false
      },
      {
        text: "CNP",
        value: "cnp",
        align: "start",
        sortable: true,
        filterable: false
      },
      {
        text: "CI",
        value: "ciUrl",
        align: "center",
        sortable: false,
        filterable: false
      },
      {
        text: "A urmat cursul",
        value: "hasCert",
        align: "start",
        sortable: true,
        filterable: false
      },
      {
        text: "Certificat",
        value: "certUrl",
        align: "center",
        sortable: false,
        filterable: false
      },
      {
        text: "Evaluat",
        value: "evaluat",
        align: "start",
        sortable: false,
        filterable: false
      },
      {
        text: "Scor",
        value: "scor",
        align: "center",
        sortable: false,
        filterable: false
      },
      {
        text: "Medie",
        value: "medie",
        align: "start",
        sortable: false,
        filterable: false
      },
      {
        text: "Admis",
        value: "admis",
        align: "end",
        sortable: true,
        filterable: false
      },
      {
        text: "Data Inscriere",
        value: "created",
        align: "start",
        sortable: true,
        filterable: false
      },
      {
        text: "Acțiuni",
        value: "actions",
        sortable: false,
        filterable: false
      },
      {
        text: "Formular",
        value: "data-table-expand"
      }
    ],
    expanded: [],
    judeteItems: [
      "Municipiul Bucuresti",
      "Judetul Alba",
      "Judetul Arad",
      "Judetul Arges",
      "Judetul Bacau",
      "Judetul Bihor",
      "Judetul Bistrita-Nasaud",
      "Judetul Botosani",
      "Judetul Braila",
      "Judetul Brasov",
      "Judetul Buzau",
      "Judetul Calarasi",
      "Judetul Caras-Severin",
      "Judetul Cluj",
      "Judetul Constanta",
      "Judetul Covasna",
      "Judetul Dambovita",
      "Judetul Dolj",
      "Judetul Galati",
      "Judetul Giurgiu",
      "Judetul Gorj",
      "Judetul Harghita",
      "Judetul Hunedoara",
      "Judetul Ialomita",
      "Judetul Iasi",
      "Judetul Ilfov",
      "Judetul Maramures",
      "Judetul Mehedinti",
      "Judetul Mures",
      "Judetul Neamt",
      "Judetul Olt",
      "Judetul Prahova",
      "Judetul Salaj",
      "Judetul Satu Mare",
      "Judetul Sibiu",
      "Judetul Suceava",
      "Judetul Teleorman",
      "Judetul Timis",
      "Judetul Tulcea",
      "Judetul Valcea",
      "Judetul Vaslui",
      "Judetul Vrancea"
    ],
    selectedUser: {
      nume: null,
      prenume: null,
      judet: null,
      cnp: null,
      hasCert: false,
      email: null,
      password: null,
      role: null,
      comisie: null
    },
    userDialog: false,
    deleteDialog: false,
    actionItem: null,
    expandedData: {},
    personalData: {},
    formKeys: [],
    unlockFormDialog: false
  }),

  computed: {
    items() {
      return this.$store.getters.usersItems;
    },
    loading() {
      return this.$store.getters.usersLoading;
    }
  },

  methods: {
    openForm(item) {
      this.unlockFormDialog = true;
      this.actionItem = item;
    },
    totalScore(score) {
      return Object.keys(score).reduce((acc, key) => {
        acc += score[key];
        return acc;
      }, 0);
    },
    comisii(evaluare) {
      const comisii = {};

      Object.keys(evaluare).forEach(key => {
        if (comisii[evaluare[key].comisie]) {
          comisii[evaluare[key].comisie].score += this.totalScore(evaluare[key].score);
          comisii[evaluare[key].comisie].nrmembri++;
        } else {
          comisii[evaluare[key].comisie] = { label: evaluare[key].comisie, score: 0 };
          comisii[evaluare[key].comisie].score = this.totalScore(evaluare[key].score);
          comisii[evaluare[key].comisie].nrmembri = 1;
        }
      });

      Object.keys(comisii).forEach(comisie => {
        comisii[comisie].medie = comisii[comisie].score / comisii[comisie].nrmembri;
      });

      return comisii;
    },
    downloadFile(file) {
      const link = document.createElement("a");
      link.href = file.url;
      link.download = file.name;
      link.target = "_blank";
      link.click();
      link.remove();
    },
    async expandRow(fn, arg, id) {
      fn(arg);
      if (arg) {
        const data = await this.$store.dispatch("loadUserForm", id);
        this.$set(this.expandedData, id, Object.freeze(data));
        const personal = await this.$store.dispatch("loadUserPersonal", id);
        this.$set(this.personalData, id, Object.freeze(personal));
      } else {
        delete this.expandedData[id];
        delete this.personalData[id];
      }
    },
    closeUserDialog() {
      this.userDialog = false;
      this.actionItem = null;
      this.actionItemID = null;
      this.selectedUser.nume = null;
      this.selectedUser.prenume = null;
      this.selectedUser.judet = null;
      this.selectedUser.cnp = null;
      this.selectedUser.hasCert = false;
      this.selectedUser.email = null;
      this.selectedUser.password = null;
      this.selectedUser.role = null;
      this.selectedUser.comisie = null;
    },
    openUserDialog() {
      this.userDialog = true;
    },
    editItem(item) {
      this.actionItem = item;
      this.selectedUser.nume = item.nume;
      this.selectedUser.prenume = item.prenume;
      this.selectedUser.judet = item.judet;
      this.selectedUser.cnp = item.cnp;
      this.selectedUser.hasCert = item.hasCert;
      this.selectedUser.role = item.role;
      this.selectedUser.comisie = item.comisie;
      this.openUserDialog();
    },
    deleteItem(item) {
      this.actionItem = item;
      this.deleteDialog = true;
    },
    saveDetails() {
      const payload = {};

      if (!this.actionItem) {
        payload.email = this.selectedUser.email
        payload.password = this.selectedUser.password
      }

      if (this.selectedUser.nume) {
        payload.nume = this.selectedUser.nume;
      }
      if (this.selectedUser.prenume) {
        payload.prenume = this.selectedUser.prenume;
      }
      if (this.selectedUser.judet) {
        payload.judet = this.selectedUser.judet;
      }
      if (this.selectedUser.cnp) {
        payload.cnp = this.selectedUser.cnp;
      }
      if (this.selectedUser.hasCert) {
        payload.hasCert = this.selectedUser.hasCert;
      }
      if (this.selectedUser.role) {
        payload.role = this.selectedUser.role;
      }
      if (this.selectedUser.comisie) {
        payload.comisie = this.selectedUser.comisie;
      }

      this.$store.dispatch("saveUser", { ...payload, id: this.actionItem?.id ?? null });
      this.closeUserDialog();
    },
    closeDelete() {
      this.deleteDialog = false;
      this.actionItem = null;
    },
    confirmDelete() { // it's actually toggling active state
      this.$store.dispatch("deleteUser", {
        value: !this.actionItem.deactivated,
        id: this.actionItem.id
      });
      this.closeDelete();
    },
    closeUnlockForm() {
      this.unlockFormDialog = false;
      this.actionItem = null;
    },
    async confirmUnlockForm() {
      this.$store.dispatch("openForm", this.actionItem.id);
      const data = await this.$store.dispatch("loadUserForm", this.actionItem.id);
      this.$set(this.expandedData, this.actionItem.id, Object.freeze(data));
      this.closeUnlockForm();
    },
    async reloadForm(id) {
      const data = await this.$store.dispatch("loadUserForm", id);
      this.$set(this.expandedData, id, Object.freeze(data));

      const personal = await this.$store.dispatch("loadUserPersonal", id);
      this.$set(this.personalData, id, Object.freeze(personal));
    }
  },

  mounted() {
    this.$store.dispatch("fetchUsers");
    this.formKeys = formKeys;
  }
};

/* eslint-disable no-unused-vars */
const formKeys = Object.freeze([
  { key: "draft", title: "Temporar" },
  { key: "lastSave", title: "Ultima salvare" },
  { key: "step1", title: "Pasul 1 - Dosarul candidatului" },
  { key: "step2", title: "Pasul 2 - Descriere succinta plan de afaceri" },
  { key: "step3", title: "Pasul 3 - Temele orizontale sau secundare la care contribuie intreprinderea, conform planului de afaceri" },
  { key: "step4", title: "Pasul 4 - Asistenta financiara solicitata" },
  { key: "step5", title: "Pasul 5 - Numarul de angajati si fisele de post" },
  { key: "step6", title: "Pasul 6 - Planul de afaceri" }
]);
</script>
